import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ListChecks, Play, Square, FileDown, FileSpreadsheet, Trash2, AlertTriangle, Zap, MousePointerClick, RotateCcw,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from '../components/ConfirmDialog';
import LogoMarca from '../components/takata/LogoMarca';
import * as takata from '../services/takataService';
import {
  LottoTakata, LottoInElenco, VoceLotto, MARCHE_SEGNALABILI, etichettaMarcaTakata, isCoinvolto,
} from '../services/takataService';
import {
  chiediAEstensione, contaGiro, csvLotto, esitoVoce, giroInterrotto, statoEstensione, StatoEstensione,
} from '../utils/takataLotto';

// Verifica multipla: fino a 50 telai incollati, verificati in blocco. Pensata per chi
// deve recuperare le verifiche delle pratiche già lavorate. Durante il giro non si
// producono PDF: i certificati si scaricano dopo, riga per riga, dove servono.
// Design: docs/superpowers/specs/2026-10-22-takata-verifica-multipla-design.md

const AGGIORNA_MS = 3000;
const RISVEGLIO_MS = 20000;

const GRUPPO_LABEL: Record<VoceLotto['gruppo'], string> = {
  server: 'Subito',
  automatico: 'Portale automatico',
  manuale: 'Con intervento',
  non_disponibile: 'Non verificabile',
};

function fmt(d?: string | null) {
  if (!d) return '';
  const dt = new Date(d);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

function classeEsito(v: VoceLotto) {
  if (v.stato === 'fatto') {
    if (v.esito === 'richiamo' || v.esito === 'stop_drive') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    if (v.esito === 'fonte_non_disponibile') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    if (v.esito === 'non_soggetto') return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
  }
  if (v.stato === 'in_corso') return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
  if (v.stato === 'errore' || v.stato === 'saltato' || v.gruppo === 'non_disponibile') {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  }
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

function scaricaFile(nome: string, contenuto: string, tipo: string) {
  const url = URL.createObjectURL(new Blob([contenuto], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const Tessera: React.FC<{ etichetta: string; valore: number; tono?: string }> = ({ etichetta, valore, tono }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
    <p className="text-2xs text-gray-500 dark:text-gray-400">{etichetta}</p>
    <p className={`text-base font-bold ${tono || 'text-gray-900 dark:text-white'}`}>{valore}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Nuovo elenco + elenchi precedenti

const NuovoLotto: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const iniziale = (location.state as { testo?: string } | null)?.testo || '';
  const [testo, setTesto] = useState(iniziale);
  const [invio, setInvio] = useState(false);
  const [lotti, setLotti] = useState<LottoInElenco[]>([]);

  useEffect(() => {
    let vivo = true;
    takata.getLotti().then((r) => { if (vivo) setLotti(r.items || []); }).catch(() => { /* elenco facoltativo */ });
    return () => { vivo = false; };
  }, []);

  const prepara = async () => {
    setInvio(true);
    try {
      const r = await takata.creaLotto(testo);
      navigate(`/area-riservata/takata/multipla/${r.lotto._id}`, { state: { oltreLimite: r.oltreLimite } });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Elenco non preparato: riprova.');
    } finally {
      setInvio(false);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-5">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Incolla i telai</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Fino a 50 per volta, copiati da Excel, dal gestionale o da un documento: vanno bene uno per
          riga o separati da virgole e spazi. Doppioni e righe che non sono telai vengono scartati.
        </p>
        <textarea
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          rows={8}
          placeholder={'WVWZZZ5ZZ74105657\nJTKLC164001004086\n…'}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-mono"
        />
        <div className="flex justify-end mt-3">
          <button
            onClick={prepara}
            disabled={invio || !testo.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold"
          >
            <ListChecks className="w-4 h-4" /> {invio ? 'Preparo…' : 'Prepara elenco'}
          </button>
        </div>
      </div>

      {lotti.length > 0 && (
        <>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Elenchi precedenti</h2>
          <div className="space-y-2">
            {lotti.map((l) => (
              <button
                key={l._id}
                onClick={() => navigate(`/area-riservata/takata/multipla/${l._id}`)}
                className="w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:shadow"
              >
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-semibold text-gray-900 dark:text-white">{fmt(l.createdAt)}</span>
                  <span className="text-gray-500 dark:text-gray-400">· {l.riepilogo.totale} telai</span>
                  <span className="text-gray-500 dark:text-gray-400">· {l.riepilogo.fatti} verificati</span>
                  {l.riepilogo.coinvolti > 0 && <span className="text-red-600 dark:text-red-400 font-semibold">· {l.riepilogo.coinvolti} coinvolti</span>}
                  {l.riepilogo.daFare + l.riepilogo.problemi > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">· {l.riepilogo.daFare + l.riepilogo.problemi} da completare</span>
                  )}
                </div>
                {l.operatore && <p className="text-2xs text-gray-400 dark:text-gray-500">preparato da {l.operatore}</p>}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Dettaglio di un elenco: giri, avanzamento, esiti

const DettaglioLotto: React.FC<{ id: string }> = ({ id }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const oltreLimite = (location.state as { oltreLimite?: string[] } | null)?.oltreLimite || [];
  const [lotto, setLotto] = useState<LottoTakata | null>(null);
  const [errore, setErrore] = useState('');
  const [estensione, setEstensione] = useState<StatoEstensione>(statoEstensione());
  const [giroServer, setGiroServer] = useState(false);
  const fermaServer = useRef(false);
  const [filtro, setFiltro] = useState<'tutti' | 'coinvolti' | 'da_completare'>('tutti');
  const [conferma, setConferma] = useState(false);

  const carica = useCallback(async () => {
    try {
      const r = await takata.getLotto(id);
      setLotto(r.lotto);
      setErrore('');
    } catch (e: any) {
      setErrore(e?.response?.status === 404 ? 'Elenco non trovato.' : 'Errore nel caricamento dell\'elenco.');
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  // Lo script dell'estensione puo' arrivare dopo il primo disegno della pagina.
  useEffect(() => {
    const t = setTimeout(() => setEstensione(statoEstensione()), 800);
    return () => clearTimeout(t);
  }, []);

  const giroEstensione = !!lotto?.esecuzione?.attiva && !giroInterrotto(lotto);
  const interrotto = !!lotto && giroInterrotto(lotto);

  // Mentre un giro e' in corso la tabella si aggiorna da sola.
  useEffect(() => {
    if (!giroEstensione && !giroServer) return undefined;
    const t = setInterval(carica, AGGIORNA_MS);
    return () => clearInterval(t);
  }, [giroEstensione, giroServer, carica]);

  // Durante il giro dell'estensione si chiede ogni tanto lo stato: la richiesta risveglia
  // il service worker se Chrome l'ha sospeso, e con lui la vedetta che fa scadere i telai
  // fermi (dalla 1.2.8). La risposta non serve.
  const giroDellEstensione = !!lotto?.esecuzione?.attiva;
  useEffect(() => {
    if (!giroDellEstensione || estensione === 'assente') return undefined;
    const t = setInterval(() => { chiediAEstensione('LOTTO_STATO', {}, 5000); }, RISVEGLIO_MS);
    return () => clearInterval(t);
  }, [giroDellEstensione, estensione]);

  const conteggi = useMemo(() => ({
    server: lotto ? contaGiro(lotto.voci, 'server') : 0,
    automatico: lotto ? contaGiro(lotto.voci, 'automatico') : 0,
    manuale: lotto ? contaGiro(lotto.voci, 'manuale') : 0,
  }), [lotto]);

  const avviaEstensione = async (giro: 'automatico' | 'manuale') => {
    const r = await chiediAEstensione('LOTTO_AVVIA', { lottoId: id, giro });
    if (!r.ok) {
      toast.error(String(r.error || 'Estensione non raggiungibile.'));
      return false;
    }
    await carica();
    return true;
  };

  // Gruppo «subito»: lo esegue il sito, un telaio alla volta, con lo stesso /verify della
  // verifica singola. Poi, se ci sono, passano all'estensione i portali automatici.
  const avviaSenzaIntervento = async () => {
    if (!lotto) return;
    fermaServer.current = false;
    setGiroServer(true);
    try {
      for (const v of lotto.voci.filter((x) => x.gruppo === 'server' && x.stato !== 'fatto')) {
        if (fermaServer.current) break;
        try {
          const r = await takata.verificaVoceServer(id, v);
          if (!r.done) {
            const motivo = r.mode === 'non_disponibile'
              ? 'Fonte ufficiale non raggiungibile in questo momento: riprova più tardi.'
              : (r.message || 'Verifica non riuscita: riprova.');
            await takata.statoVoceLotto(id, v.indice, 'errore', motivo);
          }
        } catch (e: any) {
          await takata.statoVoceLotto(id, v.indice, 'errore', e?.response?.data?.message || 'Verifica non riuscita: riprova.').catch(() => {});
        }
        await carica();
      }
    } finally {
      setGiroServer(false);
    }
    if (!fermaServer.current && conteggi.automatico > 0 && estensione === 'pronta') {
      await avviaEstensione('automatico');
    }
  };

  const ferma = async () => {
    fermaServer.current = true;
    if (estensione !== 'assente') await chiediAEstensione('LOTTO_FERMA');
    // Anche lato server: se l'estensione era assente o gia' ferma, la pagina non deve
    // restare in attesa di un battito che non arrivera'.
    if (lotto?.esecuzione?.attiva) await fetchEsecuzioneSpenta();
    await carica();
  };

  const fetchEsecuzioneSpenta = async () => {
    try {
      await takata.spegniEsecuzioneLotto(id);
    } catch (e) { /* nulla da fare: il giro risultera' interrotto */ }
  };

  const riprendi = async () => {
    const giro = lotto?.esecuzione?.gruppo === 'automatico' ? 'automatico' : 'manuale';
    await fetchEsecuzioneSpenta();
    await avviaEstensione(giro);
  };

  const cambiaMarca = async (v: VoceLotto, marca: string) => {
    if (!marca) return;
    try {
      const r = await takata.cambiaMarcaVoce(id, v.indice, marca);
      setLotto(r.lotto);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Marca non cambiata.');
    }
  };

  const esporta = () => {
    if (!lotto) return;
    const d = new Date(lotto.createdAt);
    const p = (n: number) => String(n).padStart(2, '0');
    scaricaFile(`Verifica-Takata-multipla-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}.csv`, csvLotto(lotto), 'text/csv;charset=utf-8');
  };

  const elimina = async () => {
    setConferma(false);
    try {
      await takata.eliminaLotto(id);
      navigate('/area-riservata/takata/multipla');
    } catch (e) {
      toast.error('Elenco non eliminato: riprova.');
    }
  };

  if (errore) return <p className="text-sm text-red-600">{errore}</p>;
  if (!lotto) return <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento…</p>;

  const occupato = giroServer || giroEstensione;
  const r = lotto.riepilogo;
  const voci = lotto.voci.filter((v) => {
    if (filtro === 'coinvolti') return v.stato === 'fatto' && !!v.esito && isCoinvolto(v.esito);
    if (filtro === 'da_completare') return v.stato !== 'fatto';
    return true;
  });
  const conPortale = conteggi.automatico + conteggi.manuale > 0;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <Tessera etichetta="Telai" valore={r.totale} />
        <Tessera etichetta="Verificati" valore={r.fatti} tono="text-blue-600 dark:text-blue-400" />
        <Tessera etichetta="Coinvolti" valore={r.coinvolti} tono="text-red-600 dark:text-red-400" />
        <Tessera etichetta="Da verificare" valore={r.daFare} />
        <Tessera etichetta="Da sistemare" valore={r.problemi} tono="text-amber-600 dark:text-amber-400" />
      </div>

      {oltreLimite.length > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-4 text-sm text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            Hai incollato più di 50 telai: {oltreLimite.length} sono rimasti fuori da questo elenco.
            <button
              onClick={() => navigate('/area-riservata/takata/multipla', { state: { testo: oltreLimite.join('\n') } })}
              className="ml-2 underline font-semibold"
            >
              Prepara un nuovo elenco con quelli
            </button>
          </div>
        </div>
      )}

      {conPortale && estensione !== 'pronta' && (
        <div className="flex items-start gap-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-3 mb-4 text-sm text-indigo-800 dark:text-indigo-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {estensione === 'assente'
              ? 'Per i telai che si verificano sul portale del costruttore serve l\'estensione UNASCA: installala dal registro delle verifiche.'
              : 'Per verificare in blocco sui portali dei costruttori serve l\'estensione aggiornata (versione 1.2.7 o successiva), già disponibile: Chrome la aggiorna da solo. Se non è ancora arrivata, chiudi e riapri Chrome e ricarica questa pagina.'}
            {' '}I telai «Subito» si verificano comunque.
          </span>
        </div>
      )}

      {interrotto && (
        <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-4 text-sm text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">Il giro si è interrotto (browser chiuso o estensione ricaricata).</span>
          <button onClick={riprendi} disabled={estensione !== 'pronta'} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold">
            <RotateCcw className="w-3.5 h-3.5" /> Riprendi
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-green-600" />
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Senza intervento</h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {conteggi.server} subito, dal server · {conteggi.automatico} sui portali che si compilano da soli.
            Si apre una finestra con un portale alla volta: lasciala in primo piano finché non si chiude.
          </p>
          <button
            onClick={avviaSenzaIntervento}
            disabled={occupato || conteggi.server + (estensione === 'pronta' ? conteggi.automatico : 0) === 0}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold"
          >
            <Play className="w-4 h-4" /> Avvia verifica senza intervento
          </button>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-1">
            <MousePointerClick className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Con intervento</h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {conteggi.manuale} telai su portali con codice di sicurezza o casella da spuntare (compresi
            quelli da rifare). Un portale alla volta: completa il codice o la spunta, al resto pensiamo noi.
          </p>
          <button
            onClick={() => avviaEstensione('manuale')}
            disabled={occupato || conteggi.manuale === 0 || estensione !== 'pronta'}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold"
          >
            <Play className="w-4 h-4" /> Avvia verifica con intervento
          </button>
        </div>
      </div>

      {occupato && (
        <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-3 mb-4 text-sm text-indigo-800 dark:text-indigo-200">
          <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse flex-shrink-0" />
          <span className="flex-1">
            Verifica in corso{giroEstensione ? ` (${lotto.esecuzione.gruppo === 'manuale' ? 'con intervento' : 'portali automatici'})` : ''}: l'elenco si aggiorna da solo.
          </span>
          <button onClick={ferma} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-indigo-300 dark:border-indigo-700 text-xs font-semibold">
            <Square className="w-3.5 h-3.5" /> Ferma
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mr-auto">Telai ({lotto.voci.length})</h2>
        <select value={filtro} onChange={(e) => setFiltro(e.target.value as typeof filtro)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
          <option value="tutti">Tutti</option>
          <option value="coinvolti">Solo coinvolti</option>
          <option value="da_completare">Da completare</option>
        </select>
        <button onClick={esporta} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:shadow">
          <FileSpreadsheet className="w-4 h-4" /> Scarica elenco
        </button>
      </div>

      <div className="space-y-2">
        {voci.map((v) => {
          const coinvolto = v.stato === 'fatto' && !!v.esito && isCoinvolto(v.esito);
          // «Non soggetto» viene dalla marca dedotta dal telaio: se la carta di circolazione
          // ne riporta un'altra (un modello rimarchiato), la si corregge e la riga si riapre.
          const nonSoggetto = v.stato === 'fatto' && v.esito === 'non_soggetto';
          const scegliMarca = nonSoggetto || (v.stato !== 'fatto' && v.stato !== 'in_corso' && (v.gruppo === 'non_disponibile' || !v.marca));
          return (
            <div key={v.indice} className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${coinvolto ? 'border-l-red-500' : v.stato === 'fatto' ? 'border-l-blue-400' : 'border-l-gray-300 dark:border-l-gray-600'}`}>
              <div className="p-3 flex items-center gap-3">
                <LogoMarca nome={v.marca} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">{v.vin}</span>
                    {v.marca && <span className="text-xs text-gray-500 dark:text-gray-400">{etichettaMarcaTakata(v.marca)}</span>}
                    <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${classeEsito(v)}`}>{esitoVoce(v)}</span>
                    {v.stato !== 'fatto' && v.gruppo !== 'non_disponibile' && (
                      <span className="text-2xs text-gray-400 dark:text-gray-500">{GRUPPO_LABEL[v.gruppo]}</span>
                    )}
                  </div>
                  {v.stato === 'fatto' && v.verifica
                    ? <p className="text-xs text-gray-400 dark:text-gray-500">{fmt(v.verifica.createdAt)} · {v.verifica.protocollo}</p>
                    : v.messaggio && <p className="text-xs text-amber-700 dark:text-amber-300">{v.messaggio}</p>}
                  {nonSoggetto && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Marca fuori dall'elenco del Ministero: non serve il certificato. Se la carta di circolazione riporta un'altra marca, correggila qui e verifica di nuovo.
                    </p>
                  )}
                  {scegliMarca && (
                    <select value="" onChange={(e) => cambiaMarca(v, e.target.value)}
                      className="mt-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-xs">
                      <option value="">{nonSoggetto ? 'Correggi la marca…' : 'Scegli la marca…'}</option>
                      {MARCHE_SEGNALABILI.map((m) => <option key={m.chiave} value={m.chiave}>{m.label}</option>)}
                    </select>
                  )}
                </div>
                {v.stato === 'fatto' && v.verifica && takata.haCertificato((v.verifica as takata.VerificaTakata).esito) && (
                  <button
                    onClick={() => takata.scaricaCertificato(v.verifica as takata.VerificaTakata)}
                    title="Scarica il certificato"
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
                  >
                    <FileDown className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {voci.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">Nessun telaio con questo filtro.</p>}
      </div>

      {(lotto.scartati.nonValidi.length > 0 || lotto.scartati.doppioni.length > 0) && (
        <div className="mt-5 text-xs text-gray-500 dark:text-gray-400 space-y-1">
          {lotto.scartati.nonValidi.length > 0 && (
            <p>
              <strong>Scartati perché non validi:</strong>{' '}
              {lotto.scartati.nonValidi.map((n) => `${n.testo} (${n.motivo})`).join(', ')}
            </p>
          )}
          {lotto.scartati.doppioni.length > 0 && (
            <p><strong>Incollati più volte (verificati una sola):</strong> {lotto.scartati.doppioni.join(', ')}</p>
          )}
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button onClick={() => setConferma(true)} disabled={occupato}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50">
          <Trash2 className="w-3.5 h-3.5" /> Elimina elenco
        </button>
      </div>

      <ConfirmDialog
        isOpen={conferma}
        onClose={() => setConferma(false)}
        onConfirm={elimina}
        title="Eliminare questo elenco?"
        message="Si elimina solo l'elenco: le verifiche già fatte restano nel registro, con il loro certificato."
        confirmText="Elimina elenco"
        type="danger"
      />
    </>
  );
};

// ---------------------------------------------------------------------------

const TakataVerificaMultiplaPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(id ? '/area-riservata/takata/multipla' : '/area-riservata/takata/registro')}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> {id ? 'Verifica multipla' : 'Registro delle verifiche'}
        </button>
        {/* Testata con foto (Higgsfield, 22/10/2026): la scrivania con l'elenco degli
            esiti, cioè quello che la pagina produce. Stessa forma della testata del registro. */}
        <div className="relative overflow-hidden rounded-2xl mb-5 bg-red-950 sm:min-h-[170px]">
          <img
            src="/images/takata/multipla-hero.webp"
            srcSet="/images/takata/multipla-hero-800.webp 800w, /images/takata/multipla-hero.webp 1600w"
            sizes="(min-width: 896px) 896px, 100vw"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover object-right"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-950/95 via-red-950/75 to-transparent sm:via-red-950/50" />
          <div className="relative p-5 sm:p-7 max-w-md text-white">
            <p className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-white/70 mb-1">
              <ListChecks className="w-3.5 h-3.5" /> Verifica Takata
            </p>
            <h1 className="text-lg font-bold">Verifica multipla</h1>
            <p className="text-xs text-white/80">
              Per recuperare in blocco le verifiche delle pratiche già lavorate. I certificati li scarichi dopo, dove servono.
            </p>
          </div>
        </div>
        {id ? <DettaglioLotto key={id} id={id} /> : <NuovoLotto />}
      </div>
    </div>
  );
};

export default TakataVerificaMultiplaPage;
